import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

// Constant and Utils
import {
  constructDateString,
  getDifferenceInDays,
} from "../../../common-utils/date-utils/DateUtils";
import { constructRedirectPath } from "../../../utils/redirect-utils/RedirectUtils";
import {
  toLocaleString,
  formatText,
} from "../../../common-utils/string-utils/StringUtils";
import { toStringWithRoundUp } from "../../../common-utils/number-utils/NumberUtils";
import { CampaignState } from "./planningConstants";
import { DATE_FORMATS } from "../../../constants/GeneralConstants";
import { getCitySpecificMediaList } from "../media-selection/MediaContainerUtil";
import {
  checkCanBuyerUserEditCampaign,
  checkIsBuyerUser,
} from "../../../utils/BuyerUtils";
import { getUpdatedCityName } from "../../../common-utils/city-utils/CityUtils";

// Components
import { CityRedirect } from "./CampaignRedirect";
import TableHeaders from "../../../components/table/TableHeaders";

/**
 * get Est Imp For Tg And Generic
 */
export function getEstImpForTgAndGeneric(cityInfo = {}, cityMedias = []) {
  const { roadStretchOtsMap = {} } = cityInfo || {};
  const isCityMediasPresent = cityMedias.length > 0;

  if (!isCityMediasPresent) {
    return { genericImp: 0, targetImp: 0 };
  }

  const estImpressionsMap = cityMedias.reduce(
    (acc, media) => {
      const { roadStretchId } = media || {};
      const stretchOtsInfo = roadStretchOtsMap[roadStretchId] || {};

      const { ots, otsLit, targetOts, targetOtsLit } = stretchOtsInfo || {};
      const finalGenericOts = otsLit ?? ots;
      const finalTargetOts = targetOtsLit ?? targetOts;

      // Total Impressions (generic, target)
      if (finalGenericOts) {
        acc.genericImp = acc.genericImp + finalGenericOts;
      }
      if (finalTargetOts) {
        acc.targetImp = acc.targetImp + finalTargetOts;
      }

      return acc;
    },
    { genericImp: 0, targetImp: 0 }
  );

  return estImpressionsMap;
}

// Section functions
function getImpTableHeaders(isLiveOrCompleteCampaign = false) {
  // live headers for impressions
  if (isLiveOrCompleteCampaign) {
    return [
      {
        title: {
          displayName: "Live Target Imp",
          className: "text-right align-top",
        },
        subTitle: {
          displayName: "Est Target Imp",
          className: "sub-text text-right",
        },
      },
      {
        title: {
          displayName: "Live General Imp",
          className: "text-right align-top",
        },
        subTitle: {
          displayName: "Est General Imp",
          className: "sub-text text-right",
        },
      },
    ];
  }

  // only Est Imp
  return [
    {
      title: {
        displayName: "Est Target Imp",
        className: "text-right align-top",
      },
    },
    {
      title: {
        displayName: "Est General Imp",
        className: "text-right align-top",
      },
    },
  ];
}

function getActionSTableHeaderS(isDraftOrReviewCampaign) {
  if (!isDraftOrReviewCampaign) {
    return [];
  }

  // adding "actions" only if DRAFT & REVIEW
  return [
    {
      title: {
        displayName: "Actions",
        className: "text-right",
      },
    },
  ];
}

function getCityDetailsHeaders(
  isDraftOrReviewCampaign,
  isLiveOrCompleteCampaign
) {
  const impressionTableHeader = getImpTableHeaders(isLiveOrCompleteCampaign);
  const actionTableHeader = getActionSTableHeaderS(isDraftOrReviewCampaign);

  return [
    {
      title: {
        displayName: "City",
        className: "",
      },
    },
    {
      title: {
        displayName: "No. of Stretches",
        className: "align-top text-right",
      },
    },
    {
      title: {
        displayName: "No. of Sites",
        className: "align-top text-right",
      },
    },
    {
      title: {
        displayName: "Start and End date",
        className: "text-right",
      },
      subTitle: {
        displayName: "Duration",
        className: "sub-text text-right",
      },
    },
    ...impressionTableHeader,
    ...actionTableHeader,
  ];
}

// Section Components
function CityName({ basicCityDetails = {}, campaignId, campaignPlanState }) {
  const { cityName = "--", cityId } = basicCityDetails || {};
  const updatedCityName = getUpdatedCityName(cityId, cityName);

  return (
    <td>
      <CityRedirect
        className="mb-0"
        campaignId={campaignId}
        cityId={cityId}
        campaignPlanState={campaignPlanState}
        displayText={updatedCityName}
      />
    </td>
  );
}

function StretchCount({ cityInfo }) {
  if (!cityInfo) {
    return null;
  }

  const { roadStretchOtsMap = {} } = cityInfo;
  const stretchCount = Object.keys(roadStretchOtsMap).length;

  const formattedStretchesCount =
    stretchCount === 0 ? 0 : formatText(toLocaleString(stretchCount));

  return (
    <td className="text-right">
      <span>{formattedStretchesCount}</span>
    </td>
  );
}

function SitesCount({ basicCityDetails = {}, campaignPlanState }) {
  const { mediaCount = 0, activeMediaCount = 0 } = basicCityDetails || {};

  // checking for live campaign
  const isLiveCampaign = campaignPlanState === CampaignState.LIVE;

  const formattedMediaCount =
    mediaCount === 0 ? 0 : formatText(toLocaleString(mediaCount));
  const formattedActiveMediaCount =
    activeMediaCount === 0 ? 0 : formatText(toLocaleString(activeMediaCount));

  return (
    <td className="text-right">
      {/* other state text */}
      {!isLiveCampaign && <p className="mb-0">{formattedMediaCount}</p>}

      {/* live state text */}
      {isLiveCampaign && (
        <p className="mb-0">
          <b>{formattedActiveMediaCount}</b> of <b>{formattedMediaCount}</b> are
          Live
        </p>
      )}
    </td>
  );
}

function Duration({ cityInfo }) {
  const { startTimestamp, endTimestamp } = cityInfo;
  const diff = getDifferenceInDays(startTimestamp, endTimestamp) - 1;
  const dateString = constructDateString(
    startTimestamp,
    endTimestamp,
    DATE_FORMATS.full_month_with_date_year
  );

  return (
    <td className="text-right">
      <div className="text-right">
        {dateString}
        <span className="sub-text">{`${diff} days`}</span>
      </div>
    </td>
  );
}

function EstTgImp({
  cityInfo = {},
  cityMedias = [],
  campaignPerformance = {},
  isShowLiveMetrics = false,
}) {
  // calculating total impression base on stretch specific medias
  const { targetImp } = getEstImpForTgAndGeneric(cityInfo, cityMedias);
  const formattedTargetImp = formatText(toStringWithRoundUp(targetImp));

  // live imp
  const { targetOtsServedTotal } = campaignPerformance || {};
  const formattedTargetLiveImp = formatText(
    toStringWithRoundUp(targetOtsServedTotal)
  );

  return (
    <td className="text-right">
      {/* only est imp */}
      {!isShowLiveMetrics && <p className="mb-0">{formattedTargetImp}</p>}

      {/* both live & est */}
      {isShowLiveMetrics && (
        <>
          <p className="mb-0">{formattedTargetLiveImp}</p>
          <p className="mb-0 sub-text">{formattedTargetImp}</p>
        </>
      )}
    </td>
  );
}

function EstGeneralImp({
  cityInfo = {},
  cityMedias = [],
  campaignPerformance = {},
  isShowLiveMetrics = false,
}) {
  // calculating total impression base on stretch specific medias
  const { genericImp } = getEstImpForTgAndGeneric(cityInfo, cityMedias);
  const formattedGenericImp = formatText(toStringWithRoundUp(genericImp));

  // live imp
  const { otsServedTotal } = campaignPerformance || {};
  const formattedGenericLiveImp = formatText(
    toStringWithRoundUp(otsServedTotal)
  );

  return (
    <td className="text-right">
      {/* only est imp */}
      {!isShowLiveMetrics && <p className="mb-0">{formattedGenericImp}</p>}

      {/* both live & est */}
      {isShowLiveMetrics && (
        <>
          <p className="mb-0">{formattedGenericLiveImp}</p>
          <p className="mb-0 sub-text">{formattedGenericImp}</p>
        </>
      )}
    </td>
  );
}

function ContinuePlanActionCell({ cityInfo = {} }) {
  const isBuyerUser = checkIsBuyerUser();
  const canBuyerUserEditCampaign = checkCanBuyerUserEditCampaign();

  // if BuyerUser don't have edit access
  if (isBuyerUser && !canBuyerUserEditCampaign) {
    return (
      <td className="text-right">
        <p className="mb-0 text-muted">
          Continue Planning
          <i
            title="User don't have edit campaign access."
            className="fa fa-exclamation-circle ml-1"
          />
        </p>
      </td>
    );
  }

  // city info
  const { campaignId, cityId, isDigital = false } = cityInfo || {};

  // params
  const params = `?_city=${cityId}`;
  const pureDoohParams = `?_city=${cityId}&_isPureDooh=true`;
  const finalParams = isDigital ? pureDoohParams : params;
  const redirectTo = `/campaign/${campaignId}/planning${finalParams}`;

  return (
    <td className="text-right">
      <div>
        <Link to={constructRedirectPath(redirectTo)} className="shadow-none">
          Continue Planning
        </Link>
      </div>
    </td>
  );
}

function CityDetailsRow({
  cityInfo = {},
  basicCityDetails = {},
  campaignId,
  campaignPlanState,
  cityMedias = [],
  campaignPerformance = {},
  isLiveOrCompleteCampaign = false,
  isDraftOrReviewCampaign = false,
}) {
  return (
    <tr>
      <CityName
        basicCityDetails={basicCityDetails}
        campaignId={campaignId}
        campaignPlanState={campaignPlanState}
      />
      <StretchCount cityInfo={cityInfo} />
      <SitesCount
        basicCityDetails={basicCityDetails}
        campaignPlanState={campaignPlanState}
      />
      <Duration cityInfo={cityInfo} />
      <EstTgImp
        cityInfo={cityInfo}
        cityMedias={cityMedias}
        campaignPerformance={campaignPerformance}
        isShowLiveMetrics={isLiveOrCompleteCampaign}
      />
      <EstGeneralImp
        cityInfo={cityInfo}
        cityMedias={cityMedias}
        campaignPerformance={campaignPerformance}
        isShowLiveMetrics={isLiveOrCompleteCampaign}
      />

      {/* only show for DRAFT & REVIEW state */}
      {isDraftOrReviewCampaign && (
        <ContinuePlanActionCell cityInfo={cityInfo} />
      )}
    </tr>
  );
}

export default function CityDetailsTable({
  planByCity = {},
  campaign = {},
  campaignPerformance = {}, // performance for LIVE Campaign
}) {
  // Selector
  const campaignMedias = useSelector(
    (state) => state.campaignMedia.campaignMedia
  );

  // campaign data
  const { id, infoByCity = {}, planStatus } = campaign || {};

  // checking for campaign state
  const isDraftCampaign = planStatus === CampaignState.DRAFT;
  const isReviewCampaign = planStatus === CampaignState.REVIEW;
  const isLiveCampaign = planStatus === CampaignState.LIVE;
  const isCompleteCampaign = planStatus === CampaignState.COMPLETED;

  // conditions
  const isLiveOrCompleteCampaign = isLiveCampaign || isCompleteCampaign;
  const isDraftOrReviewCampaign = isDraftCampaign || isReviewCampaign;

  // table headers
  const tableHeaders = getCityDetailsHeaders(
    isDraftOrReviewCampaign,
    isLiveOrCompleteCampaign
  );

  return (
    <div className="mt-5">
      <h4>{"City Highlights"}</h4>
      <div className="table-responsive mt-3">
        <table className="table table-media">
          <TableHeaders tableHeaders={tableHeaders} headerClass={"thead"} />
          <tbody>
            {Object.values(planByCity).map((cityInfo) => {
              const { cityId } = cityInfo;
              const citySpecificMedias = getCitySpecificMediaList(
                campaignMedias,
                cityId
              );

              return (
                <CityDetailsRow
                  key={cityId}
                  campaignId={id}
                  cityInfo={cityInfo}
                  basicCityDetails={infoByCity[cityId]}
                  campaignPlanState={planStatus}
                  campaignMedias={campaignMedias}
                  cityMedias={citySpecificMedias}
                  campaignPerformance={campaignPerformance[cityId]}
                  isLiveOrCompleteCampaign={isLiveOrCompleteCampaign}
                  isDraftOrReviewCampaign={isDraftOrReviewCampaign}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
